import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Translate } from 'react-localize-redux';
import Toast from './Toast';

const TRANSLATION_PREFIX = 'STATIC.PAGES.ANALYSIS_CHAT';
const TOAST_DISMISS_MS = 5000;

const useNoticeToast = (notice, onNotice = () => {}) => {
    const [open, setOpen] = useState(false);
    const handledNonceRef = useRef(0);

    useEffect(() => {
        const nonce = notice?.nonce ?? 0;
        if (!nonce || nonce === handledNonceRef.current) return;
        handledNonceRef.current = nonce;
        setOpen(true);
        onNotice(notice.sessionId);
    }, [notice]);

    useEffect(() => {
        if (!open) return undefined;
        const timeoutId = setTimeout(() => setOpen(false), TOAST_DISMISS_MS);
        return () => clearTimeout(timeoutId);
    }, [open, notice?.nonce]);

    return [open, () => setOpen(false)];
};

// The deleted / delete-failed / load-failed session toasts, shared by every page that
// renders a session list. `onSessionGone` fires when a session turned out to be gone
// (deleted elsewhere or failed to load) so the host can clean up e.g. its URL param.
const SessionNoticeToasts = ({ chat, onSessionGone = () => {} }) => {
    const [deletedOpen, closeDeleted] = useNoticeToast(chat.sessionDeletedNotice, onSessionGone);
    const [deleteFailedOpen, closeDeleteFailed] = useNoticeToast(chat.sessionDeleteError);
    const [loadFailedOpen, closeLoadFailed] = useNoticeToast(chat.sessionLoadError, onSessionGone);

    return (
        <>
            {deletedOpen && (
                <Toast onClose={closeDeleted}>
                    <Translate id={`${TRANSLATION_PREFIX}.SESSION_DELETED_TOAST`} />
                </Toast>
            )}
            {deleteFailedOpen && (
                <Toast icon="alert-triangle" onClose={closeDeleteFailed}>
                    <Translate id={`${TRANSLATION_PREFIX}.SESSION_DELETE_FAILED_TOAST`} />
                </Toast>
            )}
            {loadFailedOpen && (
                <Toast icon="alert-triangle" onClose={closeLoadFailed}>
                    <Translate id={`${TRANSLATION_PREFIX}.SESSION_LOAD_FAILED_TOAST`} />
                </Toast>
            )}
        </>
    );
};

const noticeShape = PropTypes.shape({
    sessionId: PropTypes.string,
    nonce: PropTypes.number,
});

SessionNoticeToasts.propTypes = {
    chat: PropTypes.shape({
        sessionDeletedNotice: noticeShape,
        sessionDeleteError: noticeShape,
        sessionLoadError: noticeShape,
    }).isRequired,
    onSessionGone: PropTypes.func,
};

export default SessionNoticeToasts;
